import { Column } from "@albertsons/uds/molecule/Table/Table.types";
import clsx from "clsx";
import SkeletonLoader from "@albertsons/uds/molecule/SkeletonLoader";
import { transformQuantity } from "./formatNumber";
import { IData } from "../types/data.types";

export const getDesktopTableColumns = (
  isDsd: boolean
): Column<IData<number>>[] => [
  {
    id: "label",
    label: "",
    value: (row: IData<number>) => (
      <div
        className="px-3 text-sm text-white select-none truncate text-center"
        title={`${row.label}`}
      >
        {row.label}
      </div>
    ),
  },
  {
    id: "onOrder",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title={isDsd ? "DSD Order" : "On Order"}
      >
        {isDsd ? "DSD Order" : "On Order"}
      </div>
    ),
    value: (row: IData<number>) => {
      if ("onOrder" in row || row.isLoading === 0) {
        return (
          <span className="w-full text-base px-3 flex items-center justify-center">
            {row.onOrder !== undefined
              ? transformQuantity(row.onOrder, 1)
              : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  ...(isDsd
    ? []
    : [
        {
          id: "inTransitToDC",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="In Transit to DC"
            >
              In Transit to DC
            </div>
          ),
          value: (row: IData<number>) => {
            if ("inTransitToDC" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.inTransitToDC !== undefined
                    ? transformQuantity(row.inTransitToDC, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
        {
          id: "dcInventory",
          label: (
            <div
              className="px-3 text-sm text-white font-normal select-none truncate text-center"
              title="DC Inventory"
            >
              DC Inventory
            </div>
          ),
          value: (row: IData<number>) => {
            if ("dcInventory" in row || row.isLoading === 0) {
              return (
                <span className="w-full text-base px-3 flex items-center justify-center">
                  {row.dcInventory !== undefined
                    ? transformQuantity(row.dcInventory, 1)
                    : "NA"}
                </span>
              );
            }
            return (
              <span className="block px-2">
                <SkeletonLoader skeletonTheme baseColor="#dadce2" />
              </span>
            );
          },
        },
      ]),
  {
    id: "inTransitToStore",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="In Transit to Store"
      >
        In Transit to Store
      </div>
    ),
    value: (row: IData<number>) => {
      if ("inTransitToStore" in row || row.isLoading === 0) {
        return (
          <span className="w-full text-base px-3 flex items-center justify-center">
            {row.inTransitToStore !== undefined
              ? transformQuantity(row.inTransitToStore, 1)
              : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  {
    id: "storeInventory",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="Store Inventory"
      >
        Store Inventory
      </div>
    ),
    value: (row: IData<number>) => {
      if ("storeInventory" in row || row.isLoading === 0) {
        return (
          <span className="w-full text-base px-3 flex items-center justify-center">
            {row.storeInventory !== undefined
              ? transformQuantity(row.storeInventory, 1)
              : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  {
    id: "total",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate text-center"
        title="Total"
      >
        Total
      </div>
    ),
    value: (row: IData<number>) => {
      if (row.isLoading === 0) {
        return (
          <span className="w-full text-base font-semibold px-3 flex items-center justify-center">
            {row.total !== undefined ? transformQuantity(row.total, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
];

export const getMobileTableColumns = (
  label: string,
  isExtraSmall: boolean,
  loading: number
): Column<IData>[] => [
  {
    id: "label",
    label: "",
    value: (row: IData) => (
      <span
        className={clsx(
          "w-full px-2 text-white flex items-center justify-center",
          isExtraSmall ? "text-sm" : "text-base",
          row.className ?? ""
        )}
      >
        {row.label}
      </span>
    ),
  },
  {
    id: "units",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
        title={label}
      >
        {label}
      </div>
    ),
    value: (row: IData) => {
      if (row.units || loading === 0) {
        return (
          <span
            className={clsx(
              "px-2 truncate flex items-center justify-center",
              isExtraSmall ? "text-sm" : "text-base",
              row.className ?? ""
            )}
          >
            {row.units !== undefined ? transformQuantity(row.units, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  {
    id: "doh",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
        title="DOH"
      >
        DOH
      </div>
    ),
    value: (row: IData) => {
      if (row.doh || loading === 0) {
        return (
          <span
            className={clsx(
              "px-2 truncate flex items-center justify-center",
              isExtraSmall ? "text-sm" : "text-base",
              row.className ?? ""
            )}
          >
            {row.doh !== undefined ? transformQuantity(row.doh, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
  {
    id: "its",
    label: (
      <div
        className="px-3 text-sm text-white font-normal select-none truncate flex items-center justify-center"
        title="ITS"
      >
        ITS
      </div>
    ),
    value: (row: IData) => {
      if (row.its || loading === 0) {
        return (
          <span
            className={clsx(
              "px-2 truncate flex items-center justify-center",
              isExtraSmall ? "text-sm" : "text-base",
              row.className ?? ""
            )}
          >
            {row.its !== undefined ? transformQuantity(row.its, 1) : "NA"}
          </span>
        );
      }
      return (
        <span className="block px-2">
          <SkeletonLoader skeletonTheme baseColor="#dadce2" />
        </span>
      );
    },
  },
];
